import { BarChart } from '@mui/x-charts/BarChart';
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { IconButton, Stack, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from 'react';
import moment from 'moment';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import NullCheck from 'components/NullCheck';
import { API_DATE_FORMAT, DATE_FOR_API_FORMAT } from 'config/const';

const ChartHoursAvg = ({ data, getDateRanges, setGridDataList, padding }) => {
  const theme = useTheme();
  const { bt } = useBizTypeTranslation();
  const [daysList, setDaysList] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayFormat, setDayFormat] = useState(null);
  const [list, setList] = useState([]);
  const [minTemp, setMinTemp] = useState(0);
  const [maxTemp, setMaxTemp] = useState(0);

  useEffect(() => {
    if (data) {
      const days = getDateRanges(moment(data?.dates?.start), moment(data?.dates?.end), 'day')
      setDaysList(days)
      setSelectedDay(days[0]);

    }
  }, [data]);

  useEffect(() => {
    if (selectedDay && data) {
      const hourlyData = prepare5MinTempDataForDate(data?.list, selectedDay.startDate);
      setList(hourlyData);
      calculateTempBounds(hourlyData.y1);
      const [month, day, year] = selectedDay?.startDate?.split("-");
      setDayFormat(`${day}.${month}.${year}`)
    }
  }, [selectedDay, data]);

  const calculateTempBounds = (values) => {
    if (!values || values.length === 0) {
      setMinTemp(0);
      setMaxTemp(0);
      return;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    // מצב 1: הכל מינוס
    if (max <= 0) {
      setMinTemp(max);
      setMaxTemp(min - 1);
      return;
    }

    // מצב 2: הכל פלוס
    if (min >= 0) {
      setMinTemp(min - 1);
      setMaxTemp(max);
      return;
    }

    // מצב 3: מעורב מינוס ופלוס
    const absMax = Math.max(Math.abs(min), Math.abs(max));

    setMinTemp(-absMax);
    setMaxTemp(absMax);
  };

  const onSelectedDayChange = (type) => {
    if (type === "next") {
      const currentIndex = daysList.findIndex(day => day.startDate === selectedDay.startDate);
      if (currentIndex < daysList.length - 1) {
        setSelectedDay(daysList[currentIndex + 1]);
      }
    }
    else if (type === "prev") {
      const currentIndex = daysList.findIndex(day => day.startDate === selectedDay.startDate);
      if (currentIndex > 0) {
        setSelectedDay(daysList[currentIndex - 1]);
      }
    }
  };

  const prepare5MinTempDataForDate = (data, dateStr) => {

    const x = [];
    const y1 = [];
    const y2 = [];

    const fiveMinMap = {};

    const targetDate = moment(dateStr, DATE_FOR_API_FORMAT);

    data.forEach((item) => {
      const created = moment.utc(item.created).local();

      if (!created.isSame(targetDate, "day")) return;
      const minute = Math.floor(created.minute() / 5) * 5;
      const timeKey = created.clone().minute(minute).second(0).millisecond(0).format('HH:mm');

      if (!fiveMinMap[timeKey]) {
        fiveMinMap[timeKey] = {
          temp: item.temp ?? 0,
          isAlert: item?.isAlert ?? false,
        };
      }
    });

    let newGridDataList = [];
    let id = 1;

    // 24 שעות * 12 נקודות (כל 5 דקות)
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) {
        const time = moment()
          .startOf('day')
          .add(h, 'hours')
          .add(m, 'minutes')
          .format('HH:mm');

        x.push(time);

        const temps = fiveMinMap[time] || { temp: 0 };
        y1.push(temps.temp);

        newGridDataList.push({
          id: id++,
          time,
          temp: temps.temp,
          isAlert: temps.isAlert,
        });
      }
    }

    setGridDataList(newGridDataList);
    return { x, y1, y2 };
  }

  return (
    <Stack width='100%' justifyContent='center' padding={padding ? padding : 1}>
      <Stack paddingX={1} width='100%' direction={{ xs: "column-reverse", md: "row" }} justifyContent='space-between' alignItems={'center'}>

        <NullCheck valueForCheck={daysList?.length > 1} nullToDiv>
          <Stack direction='row' alignItems='center' spacing={1}>

            <IconButton
              disabled={selectedDay === daysList[0]}
              onClick={() => onSelectedDayChange('prev')}
              size='small'
            >
              <ArrowForwardIos fontSize='small' />
            </IconButton>

            <Typography>{dayFormat}</Typography>

            <IconButton
              onClick={() => onSelectedDayChange('next')}
              size='small'
              disabled={selectedDay === daysList[daysList?.length - 1]}
            >
              <ArrowBackIos fontSize='small' />
            </IconButton>
            <Stack />
          </Stack>
        </NullCheck>

        <Typography variant="h6" textAlign="center">
          {moment(data?.dates?.start).format(API_DATE_FORMAT)} - {moment(data?.dates?.end).format(API_DATE_FORMAT)}
        </Typography>

      </Stack>

      <NullCheck valueForCheck={list?.x?.length > 0}>
        <BarChart
          margin={{ top: 30, right: 5, bottom: 30, left: 30 }}
          xAxis={[{ data: list?.x, scaleType: 'band' }]}
          yAxis={[
            {
              min: minTemp,
              max: maxTemp,
            },
          ]}

          series={[{
            data: list?.y1, label: bt('TEMP'),
            valueFormatter: (value) => `${value}°`, color: theme.palette.primary.main
          },
          ]}
          height={300}
          slotProps={{ legend: { hidden: true } }}
        />
      </NullCheck>
    </Stack>
  )
}
export default ChartHoursAvg